import { TFunction } from 'i18next';

import { formatMonthName } from '@/utils/date-formatters';

import {
  TimeOffChangedFieldNameDTO,
  TimeOffChangedFieldValueKeyDTO,
} from '../../types/dto/time-off-change-field-name.dto';

/** Fields whose numeric value represents a day count, shown as "N days". */
const DAY_COUNT_FIELDS: string[] = [
  TimeOffChangedFieldNameDTO.NUMBER_OF_DAYS,
  TimeOffChangedFieldNameDTO.CARRYOVER_DAYS,
  TimeOffChangedFieldNameDTO.MAXIMUM_BALANCE_LIMIT,
  TimeOffChangedFieldNameDTO.NEGATIVE_BALANCE_LIMIT,
];

const VALUE_KEY_LABEL: Record<TimeOffChangedFieldValueKeyDTO, string> = {
  [TimeOffChangedFieldValueKeyDTO.YEARLY]: 'pages.timeOff.details.changesTab.valueKeys.yearly',
  [TimeOffChangedFieldValueKeyDTO.MONTHLY]: 'pages.timeOff.details.changesTab.valueKeys.monthly',
  [TimeOffChangedFieldValueKeyDTO.TRUE]: 'common:yes',
  [TimeOffChangedFieldValueKeyDTO.FALSE]: 'common:no',
  [TimeOffChangedFieldValueKeyDTO.UNKNOWN]: 'pages.timeOff.details.changesTab.valueKeys.unknown',
};

const isValueKey = (value: string): value is TimeOffChangedFieldValueKeyDTO =>
  (Object.values(TimeOffChangedFieldValueKeyDTO) as string[]).includes(value);

/**
 * Formats a time-off changed-field value for display.
 *
 * Handles enum/boolean value keys (YEARLY/MONTHLY/TRUE/FALSE), day-count fields
 * ("N days"), and the renewal month (numeric month -> month name).
 */
export const formatFieldValue = (value: string | null, field: string, t: TFunction): string => {
  if (!value) return '';

  // Localizable value keys (allocation format, booleans).
  if (isValueKey(value)) {
    return t(VALUE_KEY_LABEL[value]);
  }

  if (DAY_COUNT_FIELDS.includes(field)) {
    return t('pages.timeOff.details.changesTab.valueDisplay.daysCount', { count: Number(value) });
  }

  // Renewal month is a 1-based month index; render its name.
  if (field === TimeOffChangedFieldNameDTO.RENEWAL_MONTH) {
    const monthName = formatMonthName(Number(value));
    if (monthName) return monthName;
  }

  return value;
};
